import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import LetsVerifyStep from './LetsVerifyStep';
import CameraPermission from './CameraPermission';
import FaceLiveness from './FaceLiveness';
import { RootState } from '../../../store';
import { ConnectPusher } from '../../../contexts/PusherContext';
import {
  facelivenessSkipAction,
  startSeflieVerificationStep,
  setShowSelfieVerificationWall,
  updateProfileLivenessStatus,
  facelivenessSubmitAction,
  setSVWallByGetSelfieVerified,
} from '../../../actions/profileActions';
import ThankYouStep from './ThankyouStep';
import { useJoin } from '../../../contexts/JoinContext';
import ConfirmationModal from './modals/ConfirmationModal';
import SuccessModal from './modals/SuccessModal';
import TryAgainModal from './modals/TryAgainModal';
import MaxAttemptModal from './modals/MaxAttemptModal';
import ConfirmVerificationModal from '../../../modules/modals/verification/ConfirmVerificationModal';
import { mixpanelTrackEvent } from '../../../sdk/MixpanelSDK';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { QA_SIMULATE_LIVENESS } from '../../../config/constants';
import { isQAautomation } from '../../../utils/helpers';

const SelfieVerificationCheck: React.FC<{
  isJoinFlow: boolean;
  showVerifyWall: boolean;
  setShowVerifyWall: Dispatch<SetStateAction<boolean>>;
}> = ({ isJoinFlow, showVerifyWall = false, setShowVerifyWall }) => {
  const { nextStep, setSkippedStepCounter, currentStepIndex, setSVStatus } = useJoin();

  const uploadSnackbar = useSelector((state: RootState) => state.uploadSnackbar);
  const { liveness, facelivenessVerifyLoader, hash_id, facelivenessStatus, isSVWallByGetSelfieVerified } = useSelector(
    (state: RootState) => state.profile
  );
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showMaxAttemptModal, setShowMaxAttemptModal] = useState<boolean>(false);
  const [selfieVerificationStep, setSelfieVerificationStep] = useState<string>(
    isSVWallByGetSelfieVerified ? 'camera_permission' : 'lets_verify'
  );
  const [showIdvConfirmModal, setShowIdvConfirmModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [myQueryParams, setMyQueryParams] = useSearchParams();

  // Page reload/refresh confirmation handler
  useEffect(() => {
    if (uploadSnackbar?.items?.length > 0 && !uploadSnackbar?.items?.every(item => item.status === 'success')) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
        return '';
      };

      // Add event listener
      window.addEventListener('beforeunload', handleBeforeUnload);

      // Cleanup on component unmount
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [uploadSnackbar]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    // qa simulation
    if (
      !facelivenessVerifyLoader &&
      (isQAautomation() || window.location.host.includes('test') || window.location.host.includes('localhost')) &&
      queryParams.has(QA_SIMULATE_LIVENESS) &&
      liveness?.status !== 'pending'
    ) {
      const origin = isJoinFlow ? 'Onboarding' : 'SV Wall';

      dispatch(
        facelivenessSubmitAction(
          hash_id,
          {
            Status: 'Success',
          },
          origin
        )
      );
      handleFaceLivelinessComplete();
    }

    if (isJoinFlow) {
      if (liveness?.status === 'pending') {
        setSelfieVerificationStep('thank_you');
      }

      if (isQAautomation() && liveness?.status === 'approved') {
        setSelfieVerificationStep('thank_you');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(facelivenessStatus)) {
      if (!['DENY', 'ERROR'].includes(facelivenessStatus?.recommendation)) {
        if (isJoinFlow) {
          //join flow - check qr code -> sv step completed and redirect to thank you screen
          setSelfieVerificationStep('thank_you');
        } else {
          setSelfieVerificationStep('none');
        }
      } else {
        isJoinFlow ? setSelfieVerificationStep('lets_verify') : setSelfieVerificationStep('none');
      }

      if (facelivenessStatus?.code) {
        setShowMaxAttemptModal(true);
      }
    }
  }, [facelivenessStatus]);

  const removeQueryString = () => {
    if (myQueryParams.get('showSVWall')) {
      myQueryParams.delete('showSVWall');
      setMyQueryParams(myQueryParams);
    }
  };

  const handleContinueClick = () => {
    setSelfieVerificationStep('camera_permission');
    dispatch(startSeflieVerificationStep(true));
    removeQueryString();
  };

  const handleSkipButton = () => {
    handleSVModalClose();
    removeQueryString();
  };

  const handleSVModalClose = () => {
    setShowConfirmModal(true);
    setShowVerifyWall && setShowVerifyWall(false);
  };

  const handleBackButtonClick = () => {
    if (isSVWallByGetSelfieVerified) {
      document.body.style.overflow = 'unset';
      setSelfieVerificationStep('camera_permission');
      dispatch(setSVWallByGetSelfieVerified(false));
      dispatch(setShowSelfieVerificationWall(false));
      return;
    }
    setSelfieVerificationStep('lets_verify');
    dispatch(startSeflieVerificationStep(false));
  };

  const handleRedirectJoin = () => {
    if (isJoinFlow) {
      nextStep();
      setSkippedStepCounter((prev: any) => ({ ...prev, [currentStepIndex]: false }));
      sessionStorage.removeItem('onboarding');

      const query = new URLSearchParams(window.location.search);
      const source = query.get('source');

      if (source) navigate('/join', { replace: true });
    }
  };

  const handleFaceLivelinessComplete = () => {
    dispatch(startSeflieVerificationStep(false));
    if (isSVWallByGetSelfieVerified) {
      document.body.style.overflow = 'unset';
      dispatch(setSVWallByGetSelfieVerified(false));
    }
  };

  const handleCloseConfirmModal = () => {
    dispatch(facelivenessSkipAction(hash_id));
    setShowConfirmModal(false);

    if (isJoinFlow) {
      nextStep();
      setSkippedStepCounter((prev: any) => ({ ...prev, [currentStepIndex]: true }));
      setSVStatus('skipped');
      sessionStorage.removeItem('onboarding');
    }

    dispatch(setShowSelfieVerificationWall(false));
    dispatch(updateProfileLivenessStatus());
  };

  const handleNeverMindClick = () => {
    setShowConfirmModal(false);
    setShowVerifyWall && setShowVerifyWall(true);
  };

  const handleThanksNextClick = () => {
    setSVStatus('done');
    handleRedirectJoin();
  };

  //do not show back button if scanned or coming from qr code
  const showSkippable = liveness?.skippable;
  const showSuccessModal =
    !isJoinFlow && !isEmpty(facelivenessStatus) && facelivenessStatus?.recommendation === 'APPROVE';
  const showTryAgainModal =
    !isEmpty(facelivenessStatus) && ['DENY', 'ERROR'].includes(facelivenessStatus?.recommendation);

  const renderSelfieVerificationSteps = (verifyWall: boolean): ReactElement => {
    switch (selfieVerificationStep) {
      case 'lets_verify':
        return (
          <LetsVerifyStep
            showVerifyModal={verifyWall}
            handleContinueClick={handleContinueClick}
            showSkippable={showSkippable}
            handleSkipButton={handleSkipButton}
            isJoinFlow={isJoinFlow}
            handleClose={handleSVModalClose}
          />
        );
      case 'camera_permission':
        return (
          <CameraPermission
            setFaceLinenessStep={() => {
              setSelfieVerificationStep('faceliveness_check');
            }}
            handleBackButtonClick={handleBackButtonClick}
            handleFaceLivelinessComplete={handleFaceLivelinessComplete}
            handleRedirectJoin={handleRedirectJoin}
            isJoinFlow={isJoinFlow}
          />
        );
      case 'faceliveness_check':
        return (
          <FaceLiveness
            handleBack={handleBackButtonClick}
            handleFaceLivelinessComplete={handleFaceLivelinessComplete}
            hashId={hash_id}
            loader={facelivenessVerifyLoader}
            isJoinFlow={isJoinFlow}
          />
        );
      case 'none':
        return <></>;
      case 'thank_you':
        return <ThankYouStep handleNextClick={handleThanksNextClick} />;
      default:
        return (
          <LetsVerifyStep
            showVerifyModal={verifyWall}
            handleContinueClick={handleContinueClick}
            showSkippable={showSkippable}
            handleSkipButton={handleSkipButton}
            isJoinFlow={isJoinFlow}
            handleClose={handleSVModalClose}
          />
        );
    }
  };

  return (
    <>
      {renderSelfieVerificationSteps(showVerifyWall)}

      <ConfirmationModal
        showConfirmModal={showConfirmModal}
        handleNevermindClick={handleNeverMindClick}
        handleClose={handleCloseConfirmModal}
      />

      <SuccessModal showSuccessModal={showSuccessModal} />

      <TryAgainModal
        showTryAgainModal={showTryAgainModal}
        isJoinFlow={isJoinFlow}
        isError={facelivenessStatus?.recommendation === 'ERROR'}
        handleTryAgainClose={handleRedirectJoin}
        showSkippable={showSkippable}
        hash_id={hash_id}
      />

      <MaxAttemptModal
        showMaxAttemptModal={showMaxAttemptModal}
        hash_id={hash_id}
        setShowMaxAttemptModal={setShowMaxAttemptModal}
        setShowIdvConfirmModal={setShowIdvConfirmModal}
        facelivenessStatus={facelivenessStatus}
        isJoinFlow={isJoinFlow}
        handleRedirectJoin={handleRedirectJoin}
      />

      <ConfirmVerificationModal
        onClose={() => {
          document.body.style.overflow = 'unset';
          setShowIdvConfirmModal(false);
          handleRedirectJoin();
        }}
        contentLabel={'Confirm ID Verification'}
        isOpen={showIdvConfirmModal}
        onConfirm={() => {
          setShowIdvConfirmModal(false);
          mixpanelTrackEvent({
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            eventName: EVENT_NAMES.BUY_VERIFICATION_PAGE_VIEW,
            payload: {},
          });

          navigate('/idv-packages?step=payment&package_id=idv_last_minute');
        }}
      />
    </>
  );
};

export default ConnectPusher(SelfieVerificationCheck);
